import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../../layouts';
import Page from '../../../../components/Page';
import axiosInstance from '../../../../utils/axios';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { isMaster } from '../../../../utils/perfil';
import { Num } from '../../../../components/gps';
import Modal, { BotaoSecundario, Campo, classesEntrada } from '../../../../components/gps/Modal';
import { cn } from '../../../../lib/utils';
import TituloTela from '../../../../components/gps/TituloTela';
import { resumoImeis } from '../../../../utils/resumoImeis';

/* ============================================================================
   Configurar dispositivos - visual novo (shadcn + Tailwind).

   Reescrita no lugar. Tela de FERRAMENTAS de suporte, so para MASTER.

   As CINCO ferramentas da tela antiga, com os mesmos endpoints:
     1. Vincular dispositivo   POST /dispositivos/registrar   { deviceId|imei|serial }
     2. Registrar IMEIs        POST /dispositivos/imei         { lista }
     3. Limpar cache (crash)   POST /dispositivos/clearCrash   { deviceId, packageName }
     4. Deletar dispositivo    DELETE /dispositivos/:deviceId
     5. Recuperar travado      POST /dispositivos/relinquish-ownership { deviceId|imei|serial }

   O GUARD DE MASTER FOI PRESERVADO, incluindo o redirecionamento: quem nao e
   Master e mandado para a Home com aviso. Esconder nao basta - a rota e
   acessivel por URL.

   ---------------------------------------------------------------------------
   A CONFIRMACAO DUPLA DE "RECUPERAR TRAVADO" FOI PRESERVADA INTEGRALMENTE, e e a
   parte mais importante desta tela. A acao envia `RELINQUISH_OWNERSHIP` ao Google:

     - remove a gestao do EMM;
     - FORCA factory reset, ignorando `factoryResetDisabled`;
     - libera o FRP da conta gerenciada;
     - exige o aparelho ONLINE.

   Ou seja, apaga o aparelho e o tira da frota, sem desfazer. O fluxo antigo pedia
   confirmacao e depois exigia DIGITAR o identificador de novo. As duas etapas
   continuam aqui - a segunda com comparacao exata, e o botao so habilita quando o
   texto confere.

   Deletar dispositivo tambem manteve a confirmacao.

   Os SweetAlert2 sairam; as confirmacoes usam o `Modal` do padrao novo.
   ============================================================================ */

const TIPOS = [
  { valor: 'deviceId', rotulo: 'Device ID' },
  { valor: 'imei', rotulo: 'IMEI' },
  { valor: 'serial', rotulo: 'Serial' },
] as const;

type Tipo = (typeof TIPOS)[number]['valor'];

ConfigurarDispositivosPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function ConfigurarDispositivosPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const ehMaster = isMaster(user);

  // 1. Vincular
  const [tipoVinculo, setTipoVinculo] = useState<Tipo>('deviceId');
  const [valorVinculo, setValorVinculo] = useState('');
  const [ocupadoVinculo, setOcupadoVinculo] = useState(false);

  // 2. IMEIs
  const [imeis, setImeis] = useState('');
  const [ocupadoImeis, setOcupadoImeis] = useState(false);

  // 3. Cache
  const [deviceIdCache, setDeviceIdCache] = useState('');
  const [packageCache, setPackageCache] = useState('');
  const [ocupadoCache, setOcupadoCache] = useState(false);

  // 4. Deletar
  const [deviceIdDelete, setDeviceIdDelete] = useState('');
  const [confirmarDelete, setConfirmarDelete] = useState(false);
  const [ocupadoDelete, setOcupadoDelete] = useState(false);

  // 5. Recuperar travado (RELINQUISH_OWNERSHIP)
  const [tipoRecuperar, setTipoRecuperar] = useState<Tipo>('deviceId');
  const [valorRecuperar, setValorRecuperar] = useState('');
  const [etapaRecuperar, setEtapaRecuperar] = useState<0 | 1 | 2>(0);
  const [repetirRecuperar, setRepetirRecuperar] = useState('');
  const [ocupadoRecuperar, setOcupadoRecuperar] = useState(false);

  // Guard: nao-Master vai para a Home com aviso. A rota e acessivel por URL,
  // entao esconder o item de menu nao basta.
  useEffect(() => {
    if (user && !ehMaster) {
      enqueueSnackbar('Acesso restrito a usuários Master.', { variant: 'warning' });
      router.replace(PATH_DASHBOARD.root);
    }
  }, [user, ehMaster, enqueueSnackbar, router]);

  const ok = (msg: string) => enqueueSnackbar(msg, { variant: 'success', style: { whiteSpace: 'pre-line' } });
  const falhou = (e: any) =>
    enqueueSnackbar(e?.response?.data?.message || 'Ops, ocorreu um erro no sistema.', {
      variant: 'error',
      style: { whiteSpace: 'pre-line' },
    });

  const listaImeis = useMemo(
    () =>
      imeis
        .split(/\r?\n|,|;|\s+/)
        .map((i) => i.trim())
        .filter(Boolean),
    [imeis]
  );

  const rotuloTipo = (t: Tipo) => TIPOS.find((x) => x.valor === t)?.rotulo ?? t;

  async function vincular() {
    if (!valorVinculo.trim()) return;
    setOcupadoVinculo(true);
    try {
      await axiosInstance.post('/dispositivos/registrar', { [tipoVinculo]: valorVinculo.trim() });
      ok('Dispositivo vinculado.');
      setValorVinculo('');
    } catch (e) {
      falhou(e);
    } finally {
      setOcupadoVinculo(false);
    }
  }

  async function registrarImeis() {
    if (!listaImeis.length) return;
    setOcupadoImeis(true);
    try {
      const { data } = await axiosInstance.post('/dispositivos/imei', { lista: listaImeis });
      ok(resumoImeis(data, listaImeis.length));
      setImeis('');
    } catch (e) {
      falhou(e);
    } finally {
      setOcupadoImeis(false);
    }
  }

  async function limparCache() {
    if (!deviceIdCache.trim() || !packageCache.trim()) return;
    setOcupadoCache(true);
    try {
      await axiosInstance.post('/dispositivos/clearCrash', {
        deviceId: deviceIdCache.trim(),
        packageName: packageCache.trim(),
      });
      // O Google so ENFILEIRA o CLEAR_APP_DATA; aparelho offline apaga quando voltar.
      ok('Comando enviado. Os dados do aplicativo são apagados quando o aparelho estiver online.');
    } catch (e) {
      falhou(e);
    } finally {
      setOcupadoCache(false);
    }
  }

  async function deletar() {
    if (!deviceIdDelete.trim()) return;
    setOcupadoDelete(true);
    try {
      await axiosInstance.delete(`/dispositivos/${encodeURIComponent(deviceIdDelete.trim())}`);
      ok('Dispositivo deletado.');
      setDeviceIdDelete('');
      setConfirmarDelete(false);
    } catch (e) {
      falhou(e);
    } finally {
      setOcupadoDelete(false);
    }
  }

  async function recuperar() {
    const valor = valorRecuperar.trim();
    if (!valor || repetirRecuperar.trim() !== valor) return;
    setOcupadoRecuperar(true);
    try {
      await axiosInstance.post('/dispositivos/relinquish-ownership', { [tipoRecuperar]: valor });
      ok('Comando enviado. O aparelho será apagado quando estiver online.');
      setValorRecuperar('');
      setRepetirRecuperar('');
      setEtapaRecuperar(0);
    } catch (e) {
      falhou(e);
    } finally {
      setOcupadoRecuperar(false);
    }
  }

  if (user && !ehMaster) {
    return (
      <Page title="Configurar dispositivos">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2">
          <div className="rounded-lg border border-line bg-danger-soft p-4 text-[12.5px] text-danger">
            Acesso restrito a usuários <strong>Master</strong>.
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page title="Configurar dispositivos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5">
          <TituloTela
            descricao={
              <>
Ferramentas de suporte que agem por <Num>DEVICE_ID</Num>, IMEI ou serial, sem passar pela
            listagem. Restrito ao perfil <strong>Master</strong>.
              </>
            }
          >
            Configurar dispositivos
          </TituloTela>
        </div>

        {/* DUAS COLUNAS, E A LARGURA DA PAGINA (10/09/2026, a pedido).

            Eram quatro cartoes empilhados dentro de 1100px centralizados: numa
            tela larga sobrava metade da janela vazia dos dois lados e as acoes
            ficavam a uma rolagem de distancia uma da outra. Sao quatro
            ferramentas independentes - vincular, registrar IMEI, limpar cache,
            deletar -, entao lado a lado nao ha sequencia a quebrar.

            `items-start` porque as alturas sao muito diferentes (o de IMEIs tem
            uma area de texto): sem isso o grid estica os vizinhos ate a altura
            do mais alto e sobra buraco dentro dos cartoes menores. */}
        <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
          {/* 1. Vincular ------------------------------------------------------ */}
          <section className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <h2 className="text-[12.5px] font-bold">Vincular dispositivo</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Registra no sistema um aparelho que já existe no Google mas não apareceu no inventário.
            </p>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Campo rotulo="Identificar por">
                <div className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
                  {TIPOS.map((t) => (
                    <button
                      key={t.valor}
                      type="button"
                      onClick={() => setTipoVinculo(t.valor)}
                      className={cn(
                        'h-7 rounded-md px-2.5 text-[12px] font-bold',
                        tipoVinculo === t.valor ? 'bg-surface text-ink shadow-card' : 'text-muted-foreground'
                      )}
                    >
                      {t.rotulo}
                    </button>
                  ))}
                </div>
              </Campo>

              <label className="block flex-1">
                <span className="mb-1 block text-[12px] font-semibold">{rotuloTipo(tipoVinculo)}</span>
                <input
                  value={valorVinculo}
                  onChange={(e) => setValorVinculo(e.target.value)}
                  className={`${classesEntrada} tabular-nums`}
                />
              </label>

              <button
                type="button"
                onClick={vincular}
                disabled={ocupadoVinculo || !valorVinculo.trim()}
                className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
              >
                {ocupadoVinculo ? 'Vinculando...' : 'Vincular'}
              </button>
            </div>
          </section>

          {/* 2. IMEIs -------------------------------------------------------- */}
          <section className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <h2 className="text-[12.5px] font-bold">Registrar IMEIs</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Libera os aparelhos para o provisionamento. Um por linha, ou separados por vírgula,
              ponto-e-vírgula ou espaço.
            </p>

            <textarea
              value={imeis}
              onChange={(e) => setImeis(e.target.value)}
              rows={4}
              placeholder={'351234567890123\n351234567890124'}
              className="mt-3 w-full rounded-lg border border-line bg-surface p-3 tabular-nums text-[12px] outline-none placeholder:font-sans placeholder:text-faint focus:border-primary"
            />

            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-[12px] text-muted-foreground">
                <Num className="font-bold text-ink">{listaImeis.length}</Num> IMEI(s) reconhecido(s)
              </span>
              <button
                type="button"
                onClick={registrarImeis}
                disabled={ocupadoImeis || !listaImeis.length}
                className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
              >
                {ocupadoImeis ? 'Registrando...' : `Registrar (${listaImeis.length})`}
              </button>
            </div>
          </section>

          {/* 3. Cache -------------------------------------------------------- */}
          <section className="rounded-lg border border-line bg-surface p-4 shadow-card">
            {/* "Cache" saiu do titulo (29/09/2026): o comando enviado e
                CLEAR_APP_DATA, que apaga login, configuracao e banco local. O
                paragrafo abaixo ja dizia a verdade; o titulo, nao - e titulo e
                o que se le antes de clicar. Mesmo ajuste ja feito no
                ModalLimparCache. */}
            <h2 className="text-[12.5px] font-bold">Limpar dados do app (crash)</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Apaga os dados locais de um aplicativo num aparelho específico. Usado quando o app entra em
              loop de erro.
            </p>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <Campo rotulo="Device ID">
                <input
                  value={deviceIdCache}
                  onChange={(e) => setDeviceIdCache(e.target.value)}
                  className={`${classesEntrada} tabular-nums`}
                />
              </Campo>
              <Campo rotulo="Pacote">
                <input
                  value={packageCache}
                  onChange={(e) => setPackageCache(e.target.value)}
                  placeholder="com.exemplo.app"
                  className={`${classesEntrada} tabular-nums`}
                />
              </Campo>
              <button
                type="button"
                onClick={limparCache}
                disabled={ocupadoCache || !deviceIdCache.trim() || !packageCache.trim()}
                className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
              >
                {ocupadoCache ? 'Limpando...' : 'Limpar'}
              </button>
            </div>
          </section>

          {/* 4. Deletar ------------------------------------------------------ */}
          <section className="rounded-lg border border-danger bg-surface p-4 shadow-card">
            {/* O TEXTO CHAMAVA UM WIPE DE "BAIXA DE INVENTARIO" (29/09/2026).

                Dizia "Remove o aparelho do inventario. Ele precisa ser
                reprovisionado para voltar." Parece arrumacao de cadastro.

                A rota e DELETE /dispositivos/:deviceId, que chega em
                `deleteDeviceId.ts` e chama
                `androidmanagement.enterprises.devices.delete()`. O proprio
                repositorio documenta o que isso faz, em
                relinquishOwnershipDevice.ts:11 - "Remove o device do enterprise
                E DISPARA WIPE COMPLETO NO APARELHO. Funciona mesmo com
                factoryResetDisabled=true na politica".

                Ou seja: apaga o telefone de quem esta em campo, e a politica de
                bloquear reset nao protege. A tela de detalhe do dispositivo
                descreve a MESMA rota corretamente ("Reset de fabrica? Apaga o
                aparelho") - era esta tela que estava fora do passo.

                Acao destrutiva descrita como inofensiva e o pior tipo de texto
                errado: nao induz duvida, induz clique. */}
            <h2 className="text-[12.5px] font-bold text-danger">Apagar aparelho (reset de fábrica)</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              <strong>Apaga tudo do telefone</strong> e o remove do inventário. É o mesmo efeito de um
              reset de fábrica, e acontece mesmo com &ldquo;bloquear restauração&rdquo; ligado na política.
              Sem volta: o aparelho precisa ser reprovisionado do zero.
            </p>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="block flex-1">
                <span className="mb-1 block text-[12px] font-semibold">Device ID</span>
                <input
                  value={deviceIdDelete}
                  onChange={(e) => setDeviceIdDelete(e.target.value)}
                  className={`${classesEntrada} tabular-nums`}
                />
              </label>
              <button
                type="button"
                onClick={() => setConfirmarDelete(true)}
                disabled={ocupadoDelete || !deviceIdDelete.trim()}
                className="h-9 rounded-lg border border-danger bg-danger-soft px-4 text-[12.5px] font-bold text-danger disabled:opacity-40"
              >
                Apagar aparelho
              </button>
            </div>
          </section>

          {/* 5. Recuperar travado ------------------------------------------- */}
          <section className="rounded-lg border-2 border-danger bg-danger-soft p-4">
            <h2 className="text-[12.5px] font-bold text-danger">
              ⚠ Recuperar dispositivo travado (factory reset forçado)
            </h2>

            <div className="mt-2 rounded-lg border border-danger bg-surface p-3 text-[12px]">
              <p className="font-bold text-danger">Esta ação é irreversível.</p>
              <p className="mt-1 text-muted-foreground">
                Envia <Num>RELINQUISH_OWNERSHIP</Num> ao Google para o aparelho informado:
              </p>
              <ul className="mt-1.5 space-y-0.5 text-muted-foreground">
                <li>• Remove a gestão do EMM</li>
                <li>
                  • Força factory reset, <strong>ignorando</strong> <Num>factoryResetDisabled</Num>
                </li>
                <li>• Libera o FRP da conta gerenciada</li>
                <li>
                  • O aparelho precisa estar <strong>online</strong>
                </li>
              </ul>
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Campo rotulo="Identificar por">
                <div className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface p-1">
                  {TIPOS.map((t) => (
                    <button
                      key={t.valor}
                      type="button"
                      onClick={() => setTipoRecuperar(t.valor)}
                      className={cn(
                        'h-7 rounded-md px-2.5 text-[12px] font-bold',
                        tipoRecuperar === t.valor ? 'bg-surface-2 text-ink shadow-card' : 'text-muted-foreground'
                      )}
                    >
                      {t.rotulo}
                    </button>
                  ))}
                </div>
              </Campo>

              <label className="block flex-1">
                <span className="mb-1 block text-[12px] font-semibold">{rotuloTipo(tipoRecuperar)}</span>
                <input
                  value={valorRecuperar}
                  onChange={(e) => setValorRecuperar(e.target.value)}
                  className={`${classesEntrada} tabular-nums`}
                />
              </label>

              <button
                type="button"
                onClick={() => {
                  setRepetirRecuperar('');
                  setEtapaRecuperar(1);
                }}
                disabled={ocupadoRecuperar || !valorRecuperar.trim()}
                className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
              >
                Recuperar (apaga tudo)
              </button>
            </div>
          </section>
        </div>
      </div>

      <Modal
        aberto={confirmarDelete}
        titulo="Apagar o aparelho?"
        descricao="Isto APAGA TUDO no telefone - é um reset de fábrica - e o remove do inventário. Não há como desfazer."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setConfirmarDelete(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmarDelete(false)} disabled={ocupadoDelete}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={deletar}
              disabled={ocupadoDelete}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {ocupadoDelete ? 'Apagando...' : 'Apagar aparelho'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Device ID</span>
          <Num className="block font-semibold">{deviceIdDelete}</Num>
        </div>
      </Modal>

      {/* ETAPA 1 da confirmacao dupla - repete o que a acao faz. */}
      <Modal
        aberto={etapaRecuperar === 1}
        titulo="Recuperar dispositivo (factory reset forçado)?"
        descricao="Ação irreversível. Leia antes de continuar."
        icone="⚠"
        largura="max-w-[540px]"
        onFechar={() => setEtapaRecuperar(0)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setEtapaRecuperar(0)} disabled={ocupadoRecuperar}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => setEtapaRecuperar(2)}
              disabled={ocupadoRecuperar}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              Sim, recuperar (apaga tudo)
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
            {rotuloTipo(tipoRecuperar)}
          </span>
          <Num className="block font-semibold">{valorRecuperar}</Num>
        </div>

        <ul className="space-y-1 text-[12px] text-muted-foreground">
          <li>• Remove a gestão do EMM</li>
          <li>
            • Força factory reset, ignorando <Num>factoryResetDisabled</Num>
          </li>
          <li>• Libera o FRP da conta gerenciada</li>
          <li>
            • O aparelho precisa estar <strong>online</strong>
          </li>
        </ul>
      </Modal>

      {/* ETAPA 2 - exige DIGITAR o identificador de novo. O botao so habilita com
          o texto exatamente igual; e a segunda barreira do fluxo antigo. */}
      <Modal
        aberto={etapaRecuperar === 2}
        titulo="Confirmação final"
        descricao={`Digite o ${rotuloTipo(tipoRecuperar)} novamente para confirmar.`}
        icone="⚠"
        largura="max-w-[500px]"
        onFechar={() => setEtapaRecuperar(0)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setEtapaRecuperar(0)} disabled={ocupadoRecuperar}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={recuperar}
              disabled={ocupadoRecuperar || repetirRecuperar.trim() !== valorRecuperar.trim()}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {ocupadoRecuperar ? 'Executando...' : 'Executar'}
            </button>
          </>
        }
      >
        <Campo rotulo={rotuloTipo(tipoRecuperar)} obrigatorio>
          <input
            value={repetirRecuperar}
            onChange={(e) => setRepetirRecuperar(e.target.value)}
            placeholder={valorRecuperar}
            autoFocus
            className={cn(
              classesEntrada,
              'tabular-nums',
              repetirRecuperar.length > 0 &&
                repetirRecuperar.trim() !== valorRecuperar.trim() &&
                'border-danger'
            )}
          />
          {repetirRecuperar.length > 0 && repetirRecuperar.trim() !== valorRecuperar.trim() && (
            <span className="mt-1 block text-[11px] text-danger">
              {rotuloTipo(tipoRecuperar)} não confere.
            </span>
          )}
        </Campo>
      </Modal>
    </Page>
  );
}
